import { z } from 'zod';
import { apiEnvelope } from '../envelope';
import { conversationStatusSchema } from './schema';

/**
 * The staff-facing unified conversation list — one page spanning both ADR-098
 * classifications for one member.
 *
 * Deliberately NOT strict (ADR-135 Rule 1): Atlas parses this response through
 * `HttpClient`, which ends every typed read in `schema.parse(...)` with no
 * tolerant reader, so a `.strict()` here would make an additive field on a
 * newer server reject at a shipped bundle rather than being stripped. The
 * care-chat response schemas carry that defect today; this contract must not
 * reproduce it.
 */

/**
 * Fields every entry carries, whichever lane it came from.
 *
 * `id` is the INTERNAL `conversations.id`, never a provider key: it is the
 * cursor's tiebreak and the list's stable React key, and the two id spaces can
 * collide. The provider key an agentic transcript is fetched by travels in the
 * agentic payload instead, because only that lane has one.
 */
const memberConversationBaseSchema = z.object({
  id: z.string().uuid(),
  /** The `created_at` of the entry's highest-`position` visible message, or the conversation's own. */
  lastActivityAt: z.string(),
  /** What the row shows as its title. Audience-relative per ADR-098 Rule 15. */
  label: z.string(),
});

/**
 * The agentic payload. Every field here is one the Sona row already depends on
 * — this list is enumerated rather than left open precisely so a reimplementation
 * cannot quietly drop one. `escalationRaised` is the field that matters most:
 * it is why a care person opens this surface at all, and its loss would be
 * invisible in the UI and material to an escalation review.
 */
export const memberConversationAgentEntrySchema = memberConversationBaseSchema.extend({
  classification: z.literal('agent'),
  /** The provider key the transcript read is keyed by — a different id space from `id`. */
  externalConversationId: z.string(),
  agentName: z.string().nullable(),
  agentType: z.string().nullable(),
  status: conversationStatusSchema,
  durationSec: z.number().int().nonnegative(),
  escalationRaised: z.boolean(),
  /** The clinician overview line, null when absent or the display gate is closed. */
  summary: z.string().nullable(),
});
export type MemberConversationAgentEntry = z.infer<typeof memberConversationAgentEntrySchema>;

/**
 * The clinical payload. Thin by comparison because the thread pane re-reads the
 * care-chat surface for the conversation itself — this entry only has to let the
 * list render a row and tell the pane which thread to open.
 */
export const memberConversationClinicalEntrySchema = memberConversationBaseSchema.extend({
  classification: z.literal('clinical'),
});
export type MemberConversationClinicalEntry = z.infer<typeof memberConversationClinicalEntrySchema>;

export const memberConversationEntrySchema = z.discriminatedUnion('classification', [
  memberConversationAgentEntrySchema,
  memberConversationClinicalEntrySchema,
]);
export type MemberConversationEntry = z.infer<typeof memberConversationEntrySchema>;

/**
 * Why the caller did or did not see clinical content on THIS request.
 *
 * One value per request, resolved `no_tier` → `granted` → `no_participation`.
 * A caller who participates in some of the member's clinical threads but not
 * others is `granted`: the field answers "was this caller able to see clinical
 * content here", not "was every row visible". Without that precedence fixed,
 * two reasonable implementations produce incomparable telemetry.
 */
export const CLINICAL_SCOPE_OUTCOMES = ['granted', 'no_tier', 'no_participation'] as const;
export const clinicalScopeSchema = z.enum(CLINICAL_SCOPE_OUTCOMES);
export type ClinicalScope = z.infer<typeof clinicalScopeSchema>;

export const memberConversationListDataSchema = z.object({
  items: z.array(memberConversationEntrySchema),
  /** Null on the last page. Opaque: clients pass it back, never parse it. */
  nextCursor: z.string().nullable(),
});
export type MemberConversationListData = z.infer<typeof memberConversationListDataSchema>;

export const memberConversationListResponseSchema = apiEnvelope(memberConversationListDataSchema);
export type MemberConversationListResponse = z.infer<typeof memberConversationListResponseSchema>;
